import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Paperclip, FileCheck2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, downloadFile } from "@/lib/api";
import { CharterPicker } from "@/components/admin/DowReviewerTools";

const ACCEPT = ".pdf,.jpg,.jpeg,.png";

async function uploadProof(id: string, file: File) {
  const fd = new FormData();
  fd.append("file", file);
  await api.post(`/dow/clearances/${id}/proof`, fd);
}

export function ProofButtons({ r, onChanged }: { r: any; onChanged: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const go = async (f: File) => {
    setBusy(true);
    try { await uploadProof(r.id, f); toast.success(`Clearance letter attached for ${r.name}`); onChanged(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  const remove = async () => {
    if (!window.confirm(`Remove the attached clearance letter (${r.proof_name}) from ${r.name}'s record? This is logged in the audit history.`)) return;
    try { await api.delete(`/dow/clearances/${r.id}/proof`); toast.success("Clearance letter removed"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={(e) => e.target.files?.[0] && go(e.target.files[0])} data-testid={`dow-proof-input-${r.id}`} />
      {r.proof_name ? (
        <span className="inline-flex items-center">
          <button className="btn-secondary btn-sm rounded-r-none" title={`${r.proof_name}, attached ${String(r.proof_uploaded_at || "").slice(0, 10)}`}
            onClick={() => downloadFile(`/dow/clearances/${r.id}/proof`, r.proof_name).catch((e) => toast.error(errMsg(e)))} data-testid={`dow-proof-view-${r.id}`}>
            <FileCheck2 className="h-4 w-4 text-[#0F766E]" /> Letter
          </button>
          <button className="btn-secondary btn-sm rounded-l-none border-l-0 px-2" title="Remove letter" onClick={remove} data-testid={`dow-proof-remove-${r.id}`}><X className="h-3.5 w-3.5" /></button>
        </span>
      ) : (
        <button className="btn-secondary btn-sm" disabled={busy} onClick={() => ref.current?.click()} title="Attach the scanned clearance letter (PDF, JPG or PNG, up to 15 MB)" data-testid={`dow-proof-attach-${r.id}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Attach letter
        </button>
      )}
    </>
  );
}

const d10 = (v?: string) => (v || "").slice(0, 10);
const plus5 = (d: string) => (/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${Number(d.slice(0, 4)) + 5}${d.slice(4) === "-02-29" ? "-02-28" : d.slice(4)}` : "");
const EMPTY = { first_name: "", last_name: "", email: "", dob: "", location: "", charter: "", address: "", note: "", position: "", clearance_type: "fingerprint", agency: "", issued: "", expires: "" };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="mb-1 block text-xs font-semibold text-[#475569]">{label}</span>{children}</label>;
}

export function ManualClearanceDialog({ edit, onClose, onSaved, notes, setNotes }: { edit: any | null | undefined; onClose: () => void; onSaved: () => void; notes?: Record<string, string>; setNotes?: (n: Record<string, string>) => void }) {
  const [f, setF] = useState<any>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [queue, setQueue] = useState(true);
  const open = edit !== undefined;
  useEffect(() => {
    if (!open) { return; }
    setFile(null);
    setQueue(true);
    setF(edit ? { first_name: edit.first_name || String(edit.name || "").split(" ")[0], last_name: edit.last_name || String(edit.name || "").split(" ").slice(1).join(" "),
      email: edit.email || "", dob: d10(edit.dob), location: edit.location || "", charter: edit.charter || "", address: edit.address || "", note: notes?.[edit.id] || "", position: edit.position || "",
      clearance_type: edit.clearance_type || "fingerprint", agency: edit.agency || "", issued: d10(edit.issued_at), expires: d10(edit.expires_at) } : EMPTY);
  }, [open, edit]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const setIssued = (e: any) => {
    const v = e.target.value;
    setF({ ...f, issued: v, expires: !f.expires || f.expires === plus5(f.issued) ? plus5(v) : f.expires });
  };
  const fullName = `${f.first_name.trim()} ${f.last_name.trim()}`.trim();
  const save = async () => {
    setBusy(true);
    try {
      let id = edit?.id;
      const { note, ...body } = f;
      if (edit) { await api.put(`/dow/clearances/${edit.id}`, body); }
      else { id = (await api.post("/dow/clearances/manual", { ...body, queue_cert: queue })).data.id; }
      if (note.trim() !== (notes?.[id] || "")) {
        const { data } = await api.put(`/dow/private-notes/${id}`, { text: note });
        setNotes?.({ ...(notes || {}), [id]: data.text });
      }
      if (file) {
        try { await uploadProof(id, file); } catch (e) { toast.error(`Saved, but the letter didn't upload: ${errMsg(e)}`); onSaved(); onClose(); setBusy(false); return; }
      }
      toast.success(edit ? "Clearance updated" : queue ? `${fullName} added. The certificate is waiting under Certificates waiting (To review tab), grouped with others for the same parish.` : `${fullName} added to approved clearances`);
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[92vh] max-w-xl flex-col gap-3 overflow-hidden p-0" data-testid="dow-manual-dialog">
        <div className="px-5 pt-5">
          <DialogHeader><DialogTitle>{edit ? `Edit clearance: ${edit.name}` : "Add an outside clearance"}</DialogTitle></DialogHeader>
          <p className="mt-1 text-xs text-[#64748B]">For clearances received outside InfoRetrieval, such as SBI/FBI fingerprinting. They join your approved list, coordinator lists and expiration reminders.</p>
        </div>
        <div className="grid flex-1 gap-2.5 overflow-y-auto px-5 pb-2 sm:grid-cols-2" data-testid="dow-manual-scroll">
          <Field label="First name *"><input className="field-input" value={f.first_name} onChange={set("first_name")} data-testid="dow-manual-first-name" /></Field>
          <Field label="Last name *"><input className="field-input" value={f.last_name} onChange={set("last_name")} data-testid="dow-manual-last-name" /></Field>
          <Field label="Email"><input className="field-input" type="email" value={f.email} onChange={set("email")} data-testid="dow-manual-email" /></Field>
          <Field label="Date of birth"><input className="field-input" type="date" value={f.dob} onChange={set("dob")} data-testid="dow-manual-dob" /></Field>
          <div className="sm:col-span-2"><CharterPicker charter={f.charter} location={f.location} onChange={(charter, location) => setF({ ...f, charter, location })} testId="dow-manual" /></div>
          <div className="sm:col-span-2"><Field label="Address"><input className="field-input" value={f.address} onChange={set("address")} data-testid="dow-manual-address" /></Field></div>
          <Field label="Ministry / position"><input className="field-input" value={f.position} onChange={set("position")} data-testid="dow-manual-position" /></Field>
          <Field label="Clearance type *">
            <select className="field-input" value={f.clearance_type} onChange={set("clearance_type")} data-testid="dow-manual-type">
              <option value="fingerprint">SBI/FBI fingerprint</option><option value="other">Other</option>
            </select>
          </Field>
          {f.clearance_type === "other" ? <Field label="Issuing agency *"><input className="field-input" value={f.agency} onChange={set("agency")} data-testid="dow-manual-agency" /></Field> : <div />}
          <Field label="Clearance date *"><input className="field-input" type="date" value={f.issued} onChange={setIssued} data-testid="dow-manual-issued" /></Field>
          <Field label="Expiration date (fills in 5 years; you can change it)"><input className="field-input" type="date" value={f.expires} onChange={set("expires")} data-testid="dow-manual-expires" /></Field>
          <div className="sm:col-span-2"><Field label="Private note (only you can see this)"><textarea className="field-input min-h-[44px]" rows={2} maxLength={4000} value={f.note} onChange={set("note")} data-testid="dow-manual-note" /></Field></div>
          <div className="sm:col-span-2">
            <Field label={edit?.proof_name ? `Clearance letter (on file: ${edit.proof_name}; choose a file to replace it)` : "Scanned clearance letter (PDF, JPG or PNG, optional)"}>
              <input className="field-input py-2" type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="dow-manual-proof" />
            </Field>
          </div>
        </div>
        {!edit && (
          <label className="mx-5 flex cursor-pointer items-start gap-2 rounded-lg bg-[#EFF6FF] p-2.5 text-xs text-[#1B365D]">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#1B365D]" checked={queue} onChange={(e) => setQueue(e.target.checked)} data-testid="dow-manual-queue-cert" />
            <span><b>Send a Certificate of Clearance to the parish's Compliance Coordinators.</b> It waits under Certificates waiting with any others for the same charter, so you can send them all in one email.</span>
          </label>
        )}
        <div className="flex justify-end gap-2 border-t border-slate-200 bg-white px-5 py-3">
          <button className="btn-secondary btn-sm" onClick={onClose} data-testid="dow-manual-cancel">Cancel</button>
          <button className="btn-primary btn-sm" disabled={busy || !f.first_name.trim() || !f.last_name.trim() || !f.issued} onClick={save} data-testid="dow-manual-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {edit ? "Save changes" : "Add clearance"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
